"use client";

import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { LIVE_STATUSES } from "@/contants";
import OkKbvMatchMinute from "@/components/OkKbvMatchMinute";
import OkOtherBetsDropdown from "../OkOtherBetsDropdown";
import { useMatches } from "@/hooks/useMatches";
import ViuGoTipsAiModal from "../../ViuGoTipsAiModal";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import OddsSection from "../../OKMatch/OddsSection";
import OddsBtnContainer from "../../OKMatch/OddsBtnContainer";
import OddsSpacer from "../../OKMatch/OddsSpacer";
import yellowCardIcon from "@/assets/images/kbv-yellow-card.svg";
import redCardIcon from "@/assets/images/kbv-red-card.svg";
import cornerIcon from "@/assets/images/kbv-corner.svg";
import arrowRightIcon from "@/assets/images/kbv-arrow-right.svg";
import "./index.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

export default function OKMatch({ match, isFirstMatch = false }) {
  const newbieMode = useSelector((state) => state.toggleReducer.newbie);
  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const { getMatchOdds, getMatchInsight } = useMatches();
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeRed,
    awayRed,
    homeYellow,
    awayYellow,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const asText = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  };

  const countOtherOdds = match?.countOtherOdds ?? 0;
  const [showOtherBets, setShowOtherBets] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const totalYellowHome = Number(homeYellow) || 0;
  const totalYellowAway = Number(awayYellow) || 0;
  const totalRedHome = Number(homeRed) || 0;
  const totalRedAway = Number(awayRed) || 0;
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef(0);

  const handleToggleOtherBets = () => {
    if (countOtherOdds <= 1) return;

    setShowOtherBets((prev) => !prev);
    if (!showOtherBets && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) {
            setOtherOddsData(data.data);
          }
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const matchTimeStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm")
      : "--";
  const matchDateStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM")
      : "--";
  const derivedLeagueName = match?.league?.name || match?.leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || "";
  const homeName = match?.homeTeam?.nameVi || match?.homeName;
  const awayName = match?.awayTeam?.nameVi || match?.awayName;
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = !isLive ? "Sắp diễn ra" : "Trực tiếp";

  return (
    <div className="relative z-0 flex cursor-pointer flex-col border-b border-[var(--theme-border)] bg-[var(--theme-foreground)] transition-colors duration-300 last:border-b-0 hover:bg-[var(--theme-accent)] last:hover:rounded-b-[12px] py-2 md:py-[15px] px-1 md:px-[20px]">
      {/* top-body */}
      <div className={`relative z-50 mb-2 md:mb-4 md:my-[5px] flex items-center justify-between ${!isFirstMatch ? "mb-[15px] md:top-[-15px]" : " md:top-[-10px]"}`}>
        <div className="flex items-center">
          {isLive && (
            <span className="mr-2 inline-block h-[6px] w-[6px] rounded-full kbv-bg-live-indicator" />
          )}
          <div className="text-[12px] md:text-[18px] kbv-text-live-match">
            <OkKbvMatchMinute
              liveScore={liveScore}
              status={statusNum}
              matchTime={match?.matchTime}
              halfStartTime={match?.halfStartTime}
              className="text-xs md:text-[18px] kbv-text-live-match"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenTipsAiModal}
            className="kbv-v27-tips-ai-btn flex items-center gap-1 rounded-[4px] px-2 py-[2px] text-[var(--theme-on-primary)] md:gap-1.5 md:px-3 md:py-1"
          >
            <img src={viugoTipsAiSparkle} alt="" className="kbv-v27-tips-ai-sparkle h-3 w-3 shrink-0 md:h-3.5 md:w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-tight md:text-[11px]">Tips AI</span>
          </button>
          {countOtherOdds > 1 && (
            <button
              type="button"
              onClick={handleToggleOtherBets}
              className="flex cursor-pointer items-center rounded-[4px] bg-[var(--theme-primary)] px-1 py-[2px] text-xs leading-none text-[var(--theme-on-primary)]"
            >
              <span className="flex items-center text-xs">
                {countOtherOdds} +
                <img src={arrowRightIcon} alt="arrow" className="ml-[5px] rotate-90 h-3.5 w-3.5" />
              </span>
            </button>
          )}
        </div>
      </div>

      {/* body-wrap */}
      <div className="flex flex-wrap">
        {/* body-left */}
        <div className="relative flex max-w-[32%] flex-[1_32%] md:flex-[1_1_32%] flex-col items-start text-[14px]">
          {/* market-team-name */}
          <div className="flex flex-col justify-center text-[15px]">
            {/* Home team */}
            <div className="mt-[17px] flex h-[35px] items-center text-[15px] text-[var(--theme-text)]">
              <div
                className={`mr-0.5 md:mr-[10px] flex h-8 md:h-[30px] w-8 md:w-[48px] shrink-0 items-center justify-center rounded-[8px] text-xs md:text-[18px] font-semibold ${isHomeWin ? "bg-[var(--theme-primary)] text-white" : "bg-[var(--theme-accent)] text-[var(--theme-text)]"}`}
              >
                {currentHomeScore}
              </div>
              <div className="mr-[5px] flex h-6 md:h-[35px] w-6 md:w-[35px] shrink-0 items-center justify-center">
                {homeLogo ? (
                  <img
                    src={homeLogo}
                    alt={match?.homeName || "home"}
                    className="h-6 md:h-[35px] w-6 md:w-[35px] rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-[35px] w-[35px] rounded-full bg-[var(--theme-border)]" />
                )}
              </div>
              <span className="line-clamp-1 w-[110px] md:w-[180px] cursor-pointer overflow-hidden text-ellipsis text-xs md:text-[18px] leading-[25px] text-[var(--theme-text)]">
                {match?.homeName || "-"}
              </span>
            </div>

            {/* Away team */}
            <div className="mt-[20px] flex h-[35px] items-center text-[15px] text-[var(--theme-text)]">
              <div
                className={`mr-0.5 md:mr-[10px] flex h-8 md:h-[30px] w-8 md:w-[48px] shrink-0 items-center justify-center rounded-[8px] text-xs md:text-[18px] font-semibold  ${isAwayWin ? "bg-[var(--theme-primary)] text-white" : "bg-[var(--theme-accent)] text-[var(--theme-text)]"}`}
              >
                {currentAwayScore}
              </div>
              <div className="mr-[5px] flex h-6 md:h-[35px] w-6 md:w-[35px] shrink-0 items-center justify-center">
                {awayLogo ? (
                  <img
                    src={awayLogo}
                    alt={match?.awayName || "away"}
                    className="h-6 md:h-[35px] w-6 md:w-[35px] rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-[35px] w-[35px] rounded-full bg-[var(--theme-border)]" />
                )}
              </div>
              <span className="line-clamp-1 w-[110px] md:w-[180px] cursor-pointer overflow-hidden text-ellipsis text-xs md:text-[18px] leading-[25px] text-[var(--theme-text)]">
                {match?.awayName || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex -mt-[93px] md:-mt-[110px] max-w-[68%] flex-[1_1_68%] flex-row items-start p-0">
          {newbieMode ? (
            <>
              {/* Column 1 */}
              <OddsSection ftLabel="" title="1" isFirstSection showTitle={isFirstMatch}>
                {/* Spacers to push button down */}
                <OddsSpacer />
                <OddsBtnContainer
                  title="Nhà"
                  value={asText(homeEuropeOdds)}
                  trend={homeEuropeTrend}
                  noHover={!homeEuropeOdds || homeEuropeOdds === "-"}
                />
              </OddsSection>

              {/* Column X */}
              <OddsSection ftLabel="" title="X" showTitle={isFirstMatch}>
                <OddsSpacer />
                <OddsBtnContainer
                  title="Hòa"
                  value={asText(instantDrawOdds)}
                  trend={drawTrend}
                  noHover={!instantDrawOdds || instantDrawOdds === "-"}
                />
              </OddsSection>

              {/* Column 2 */}
              <OddsSection ftLabel="" title="2" showTitle={isFirstMatch}>
                <OddsSpacer />
                <OddsBtnContainer
                  title="Khách"
                  value={asText(awayEuropeOdds)}
                  trend={awayEuropeTrend}
                  noHover={!awayEuropeOdds || awayEuropeOdds === "-"}
                />
              </OddsSection>
            </>
          ) : (
            <>
              {/* FT 1X2 */}
              <OddsSection ftLabel="FT" title="1X2" isFirstSection showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title="Nhà"
                  value={asText(homeEuropeOdds)}
                  trend={homeEuropeTrend}
                  noHover={!homeEuropeOdds || homeEuropeOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title="Khách"
                  value={asText(awayEuropeOdds)}
                  trend={awayEuropeTrend}
                  noHover={!awayEuropeOdds || awayEuropeOdds === "-"}
                />
                <OddsBtnContainer
                  title="Hòa"
                  value={asText(instantDrawOdds)}
                  trend={drawTrend}
                  noHover={!instantDrawOdds || instantDrawOdds === "-"}
                />
              </OddsSection>

              {/* FT Cược chấp */}
              <OddsSection ftLabel="FT" title="Cược chấp" showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title={handicapValue && handicapValue !== "-" ? `${handicapValue}` : ""}
                  value={asText(homeHandicapOdds)}
                  trend={homeHandicapTrend}
                  noHover={!homeHandicapOdds || homeHandicapOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title={handicapValue && handicapValue !== "-" ? `${handicapValue}` : ""}
                  value={asText(awayHandicapOdds)}
                  trend={awayHandicapTrend}
                  noHover={!awayHandicapOdds || awayHandicapOdds === "-"}
                />
              </OddsSection>

              {/* FT Tài/Xỉu */}
              <OddsSection ftLabel="FT" title="Tài/Xỉu" showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title={overUnderValue && overUnderValue !== "-" ? `Tài ${overUnderValue}` : ""}
                  value={asText(overOdds)}
                  trend={overTrend}
                  noHover={!overOdds || overOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title={overUnderValue && overUnderValue !== "-" ? `Xỉu ${overUnderValue}` : ""}
                  value={asText(underOdds)}
                  trend={underTrend}
                  noHover={!underOdds || underOdds === "-"}
                />
              </OddsSection>

              {/* 1H 1X2 */}
              <OddsSection ftLabel="1H" title="1X2" isFirstHalf showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title="Nhà"
                  value={asText(homeEuropeHalfOdds)}
                  trend={homeEuropeHalfTrend}
                  noHover={!homeEuropeHalfOdds || homeEuropeHalfOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title="Khách"
                  value={asText(awayEuropeHalfOdds)}
                  trend={awayEuropeHalfTrend}
                  noHover={!awayEuropeHalfOdds || awayEuropeHalfOdds === "-"}
                />
                <OddsBtnContainer
                  title="Hòa"
                  value={asText(drawHalfOdds)}
                  trend={drawHalfTrend}
                  noHover={!drawHalfOdds || drawHalfOdds === "-"}
                />
              </OddsSection>

              {/* 1H Cược chấp */}
              <OddsSection ftLabel="1H" title="Cược chấp" showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title={handicapHalfValue && handicapHalfValue !== "-" ? `${handicapHalfValue}` : ""}
                  value={asText(homeHandicapHalfOdds)}
                  trend={homeHandicapHalfTrend}
                  noHover={!homeHandicapHalfOdds || homeHandicapHalfOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title={handicapHalfValue && handicapHalfValue !== "-" ? `${handicapHalfValue}` : ""}
                  value={asText(awayHandicapHalfOdds)}
                  trend={awayHandicapHalfTrend}
                  noHover={!awayHandicapHalfOdds || awayHandicapHalfOdds === "-"}
                />
              </OddsSection>

              {/* 1H Tài/Xỉu */}
              <OddsSection ftLabel="1H" title="Tài/Xỉu" showTitle={isFirstMatch}>
                <OddsBtnContainer
                  title={overUnderHalfValue && overUnderHalfValue !== "-" ? `Tài ${overUnderHalfValue}` : ""}
                  value={asText(overHalfOdds)}
                  trend={overHalfTrend}
                  noHover={!overHalfOdds || overHalfOdds === "-"}
                  isFirst
                />
                <OddsBtnContainer
                  title={overUnderHalfValue && overUnderHalfValue !== "-" ? `Xỉu ${overUnderHalfValue}` : ""}
                  value={asText(underHalfOdds)}
                  trend={underHalfTrend}
                  noHover={!underHalfOdds || underHalfOdds === "-"}
                />
              </OddsSection>
            </>
          )}
        </div>

        {/* Mobile slider view */}
        <div className="md:hidden -mt-[90px] md:-mt-[93px] max-w-auto flex-1 overflow-hidden p-0 relative z-0">
          {newbieMode ? (
            <div className="flex min-w-full flex-row items-start gap-[2px] justify-end">
              <OddsSection ftLabel="" title="1" isFirstSection showTitle={isFirstMatch}>
                <OddsSpacer />
                <OddsBtnContainer
                  title="Nhà"
                  value={asText(homeEuropeOdds)}
                  trend={homeEuropeTrend}
                  noHover={!homeEuropeOdds || homeEuropeOdds === "-"}
                />
              </OddsSection>
              <OddsSection ftLabel="" title="X" showTitle={isFirstMatch}>
                <OddsSpacer />
                <OddsBtnContainer
                  title="Hòa"
                  value={asText(instantDrawOdds)}
                  trend={drawTrend}
                  noHover={!instantDrawOdds || instantDrawOdds === "-"}
                />
              </OddsSection>
              <OddsSection ftLabel="" title="2" showTitle={isFirstMatch}>
                <div className="h-[40px] md:h-[60px] w-full" />
                <OddsBtnContainer
                  title="Khách"
                  value={asText(awayEuropeOdds)}
                  trend={awayEuropeTrend}
                  noHover={!awayEuropeOdds || awayEuropeOdds === "-"}
                />
              </OddsSection>
            </div>
          ) : (
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const diff = touchStartX.current - e.changedTouches[0].clientX;
                if (diff > 50 && activeSlide < 1) setActiveSlide(1);
                if (diff < -50 && activeSlide > 0) setActiveSlide(0);
              }}
            >
              {/* Slide 1: FT (toàn trận) */}
              <div className="flex min-w-full flex-row items-start gap-1 md:gap-[5px] justify-end">
                <OddsSection ftLabel="FT" title="1X2" isFirstSection showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title="Nhà"
                    value={asText(homeEuropeOdds)}
                    trend={homeEuropeTrend}
                    noHover={!homeEuropeOdds || homeEuropeOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title="Khách"
                    value={asText(awayEuropeOdds)}
                    trend={awayEuropeTrend}
                    noHover={!awayEuropeOdds || awayEuropeOdds === "-"}
                  />
                  <OddsBtnContainer
                    title="Hòa"
                    value={asText(instantDrawOdds)}
                    trend={drawTrend}
                    noHover={!instantDrawOdds || instantDrawOdds === "-"}
                  />
                </OddsSection>

                <OddsSection ftLabel="FT" title="Cược chấp" showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title={handicapValue && handicapValue !== "-" ? `${handicapValue}` : ""}
                    value={asText(homeHandicapOdds)}
                    trend={homeHandicapTrend}
                    noHover={!homeHandicapOdds || homeHandicapOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title={handicapValue && handicapValue !== "-" ? `${handicapValue}` : ""}
                    value={asText(awayHandicapOdds)}
                    trend={awayHandicapTrend}
                    noHover={!awayHandicapOdds || awayHandicapOdds === "-"}
                  />
                </OddsSection>

                <OddsSection ftLabel="FT" title="Tài/Xỉu" showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title={overUnderValue && overUnderValue !== "-" ? `Tài ${overUnderValue}` : ""}
                    value={asText(overOdds)}
                    trend={overTrend}
                    noHover={!overOdds || overOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title={overUnderValue && overUnderValue !== "-" ? `Xỉu ${overUnderValue}` : ""}
                    value={asText(underOdds)}
                    trend={underTrend}
                    noHover={!underOdds || underOdds === "-"}
                  />
                </OddsSection>
              </div>

              {/* Slide 2: 1H (hiệp 1) */}
              <div className="flex min-w-full flex-row items-start gap-1 md:gap-[5px] justify-end">
                <OddsSection ftLabel="1H" title="1X2" isFirstSection showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title="Nhà"
                    value={asText(homeEuropeHalfOdds)}
                    trend={homeEuropeHalfTrend}
                    noHover={!homeEuropeHalfOdds || homeEuropeHalfOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title="Khách"
                    value={asText(awayEuropeHalfOdds)}
                    trend={awayEuropeHalfTrend}
                    noHover={!awayEuropeHalfOdds || awayEuropeHalfOdds === "-"}
                  />
                  <OddsBtnContainer
                    title="Hòa"
                    value={asText(drawHalfOdds)}
                    trend={drawHalfTrend}
                    noHover={!drawHalfOdds || drawHalfOdds === "-"}
                  />
                </OddsSection>

                <OddsSection ftLabel="1H" title="Cược chấp" showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title={handicapHalfValue && handicapHalfValue !== "-" ? `+ ${handicapHalfValue}` : ""}
                    value={asText(homeHandicapHalfOdds)}
                    trend={homeHandicapHalfTrend}
                    noHover={!homeHandicapHalfOdds || homeHandicapHalfOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title={handicapHalfValue && handicapHalfValue !== "-" ? `- ${handicapHalfValue}` : ""}
                    value={asText(awayHandicapHalfOdds)}
                    trend={awayHandicapHalfTrend}
                    noHover={!awayHandicapHalfOdds || awayHandicapHalfOdds === "-"}
                  />
                </OddsSection>

                <OddsSection ftLabel="1H" title="Tài/Xỉu" showTitle={isFirstMatch}>
                  <OddsBtnContainer
                    title={overUnderHalfValue && overUnderHalfValue !== "-" ? `Tài ${overUnderHalfValue}` : ""}
                    value={asText(overHalfOdds)}
                    trend={overHalfTrend}
                    noHover={!overHalfOdds || overHalfOdds === "-"}
                    isFirst
                  />
                  <OddsBtnContainer
                    title={overUnderHalfValue && overUnderHalfValue !== "-" ? `Xỉu ${overUnderHalfValue}` : ""}
                    value={asText(underHalfOdds)}
                    trend={underHalfTrend}
                    noHover={!underHalfOdds || underHalfOdds === "-"}
                  />
                </OddsSection>
              </div>
            </div>
          )}

          {/* Slick dots */}
          {!newbieMode && (
            <ul className="slick-dots flex items-center justify-center gap-[5px] p-0 m-0 mt-[8px] list-none pl-[25%]">
              {[0, 1].map((i) => (
                <li key={i} className={i === activeSlide ? "slick-active !ml-0" : "!ml-0"}>
                  <button
                    onClick={() => setActiveSlide(i)}
                    className="relative block h-5 w-5 border-0 bg-transparent p-1.25 text-[0px] leading-[0] text-transparent outline-none cursor-pointer"
                  >
                    <span
                      className={`absolute top-0 left-0 flex h-[25px] w-[15px] items-center justify-center text-[6px] leading-[20px] text-black ${i === activeSlide ? "opacity-75" : "opacity-25"}`}
                    >
                      <div className="bg-black w-full h-0.5"></div>
                    </span>
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* right-footer */}
      <div className="relative mt-[15px] md:mt-[25px] flex w-full items-center">
        <span className="mr-[10px] flex items-center whitespace-nowrap text-[12px] md:text-[16px] text-[var(--theme-text)]">1HT</span>
        <span className="mr-[2px] flex items-center whitespace-nowrap text-[12px] md:text-[16px] text-[var(--theme-text)]">
          ({currentHomeHalfScore}-{currentAwayHalfScore})
        </span>
        <div className="absolute right-0 flex items-center">
          {(totalRedHome > 0 || totalRedAway > 0) && (
            <div className="mr-[15px] flex items-center">
              <img src={redCardIcon} alt="red card" className="h-[18px] w-3.5" />
              <span className="mr-[2px] ml-[10px] flex items-center whitespace-nowrap text-[12px] md:text-[16px] kbv-text-vibrant-red font-semibold">
                {totalRedHome}-{totalRedAway}
              </span>
            </div>
          )}
          {(totalYellowHome > 0 || totalYellowAway > 0) && (
            <div className="flex items-center">
              <img src={yellowCardIcon} alt="yellow card" className="h-[18px] w-3.5" />
              <span className="mr-[2px] ml-[10px] flex items-center whitespace-nowrap text-[12px] md:text-[16px] text-[var(--theme-text)]">
                {totalYellowHome}-{totalYellowAway}
              </span>
            </div>
          )}
          <span className="flex items-center whitespace-nowrap text-[12px] md:text-[16px] text-[var(--theme-text)]">
            <img src={cornerIcon} alt="corner" className="h-4 w-3.5" />
            <span className="ml-[5px]">{currentHomeCorner}-{currentAwayCorner}</span>
          </span>
        </div>
      </div>

      {showOtherBets && otherOddsData && (
        <div className="mt-3">
          <OkOtherBetsDropdown
            goalOddEvenOdds={otherOddsData.goalOddEvenOdds}
            correctScoresOdds={otherOddsData.correctScoresOdds}
            totalCornersOdds={otherOddsData.totalCornersOdds}
            correctScoresHalfOdds={otherOddsData.correctScoresHalfOdds}
            totalCornersHalfOdds={otherOddsData.totalCornersHalfOdds}
            homeTeamName={match?.homeName}
            awayTeamName={match?.awayName}
          />
        </div>
      )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{ formattedDate: matchDateStr, formattedGio: matchTimeStr, isLive }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={homeName}
        homeLogo={homeLogo}
        awayName={awayName}
        awayLogo={awayLogo}
        matchDate={matchDateStr}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={scoreText}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </div>
  );
}
